import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import SvgFormat from '~helpers/svgFormat';
import Countdown from '~components/countdown';
import useDueHours from '~components/countdown/useDueHours';
import useInstantProcess from '../useInstantProcess';
import { instantBookOrderStage } from '~constants/bookings';

const InstantBookProcessBar = ({ booking }) => {
  const { t } = useTranslation();

  const {
    processes,
    bookingJourney,
    stage,
    refunds,
    confirmationDuration,
    paymentDuration,
    secondaryStage,
    paymentOnline,
    landlordConfirmedPayment,
  } = booking;
  const isNoPayment = !paymentOnline;
  const { milestoneDetails } = useInstantProcess({
    processes,
    bookingJourney,
    stage,
    isNoPayment,
    landlordConfirmedPayment,
  });

  const { dueHours } = useDueHours({ stage, confirmationDuration, paymentDuration });

  // 所有的支付全部退款
  const allPaymentRefunded =
    stage === 'CANCELLED' && secondaryStage === 'CANCEL_BY_STUDENT_ALL_REFUNDED';
  // 部分支付已经退款
  const partiallyPaymentRefunded =
    stage === 'CANCELLED' && secondaryStage === 'CANCEL_BY_STUDENT_PARTIAL_REFUNDED';
  const isStageRefunding = stage === 'REFUNDING';
  // stripe的退款
  const hasStripeRefundedRefund = refunds?.some(refund => refund?.refundMethod === 'STRIPE');
  // 非stripe的退款
  const hasNonStripeRefundedRefund = refunds?.some(refund => refund?.refundMethod !== 'STRIPE');
  return (
    <div className="instant-process-bar">
      <ul className="instant-process-bar__process-container">
        <For of={Object.keys(milestoneDetails)} index="idx" each="key">
          <li
            className={classNames(
              'instant-process-bar__item',
              `instant-process-bar__item--${milestoneDetails[key].status}`,
            )}
            key={key}
          >
            <div className="instant-process-bar__icon-bg">
              <div
                className={classNames(
                  'instant-process-bar__icon-container',
                  `instant-process-bar__icon-container--${milestoneDetails[key].status}`,
                  {
                    [`instant-process-bar__icon-container--type-${milestoneDetails[key].type}`]:
                      milestoneDetails[key].type,
                    [`instant-process-bar__icon-container--border-${milestoneDetails[key].border}`]:
                      milestoneDetails[key].border,
                    ['instant-process-bar__icon-container--not-empty']:
                      key === 'refunding' && isStageRefunding,
                  },
                )}
              >
                <SvgFormat
                  name="student-portal"
                  type={milestoneDetails[key].iconHash}
                  className={`instant-process-bar__icon instant-process-bar__icon--${key.toLowerCase()}`}
                />
              </div>
            </div>

            <div className="instant-process-bar__item-label-container">
              <p className="instant-process-bar__item-label">
                {t(`landlord.longtail_booking.process.title.${key}`)}
              </p>
              <Choose>
                <When
                  condition={key === 'refunding' && isStageRefunding && hasStripeRefundedRefund}
                >
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.title.stripe_Refunded')}
                  </p>
                </When>
                <When
                  condition={key === 'refunding' && isStageRefunding && hasNonStripeRefundedRefund}
                >
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.title.not_stripe_Refunded')}
                  </p>
                </When>
                <When condition={key === 'refunding' && allPaymentRefunded}>
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.title.all_payment_refunded')}
                  </p>
                </When>
                <When condition={key === 'refunding' && partiallyPaymentRefunded}>
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.title.partially_payment_refunded')}
                  </p>
                </When>
                {/* TODO */}
                {/* <When
                  condition={
                    isNoPayment &&
                    key === 'selecting_payment_plan' &&
                    milestoneDetails[key]?.modifierName
                  }
                >
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.stage.selecting_payment_plan', {
                      modifier_name: milestoneDetails[key]?.modifierName,
                    })}
                  </p>
                </When>
                <When
                  condition={
                    isNoPayment &&
                    key === 'completed' &&
                    milestoneDetails[key]?.secondaryStage === 'guarantor_provided'
                  }
                >
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.stage.complete.guarantor_provided', {
                      modifier_name: milestoneDetails[key]?.modifierName,
                    })}
                  </p>
                </When>
                <When
                  condition={
                    isNoPayment &&
                    key === 'completed' &&
                    milestoneDetails[key]?.secondaryStage === 'no_need_guarantor'
                  }
                >
                  <p className="instant-process-bar__item-label--stage">
                    {t('landlord.longtail_booking.process.stage.complete.no_need_guarantor', {
                      modifier_name: milestoneDetails[key]?.modifierName,
                    })}
                  </p>
                </When> */}
                <Otherwise>
                  <If
                    condition={
                      milestoneDetails[key]?.secondaryStage &&
                      milestoneDetails[key]?.secondaryStage !== 'cancel_by_student_all_refunded' &&
                      milestoneDetails[key]?.secondaryStage !== 'cancel_by_student_partial_refunded'
                    }
                  >
                    <p className="instant-process-bar__item-label--stage">
                      {t(
                        `landlord.longtail_booking.process.title.${milestoneDetails[key]?.secondaryStage}`,
                      )}
                    </p>
                  </If>
                </Otherwise>
              </Choose>

              <If condition={milestoneDetails[key]?.updatedAt}>
                <p className="instant-process-bar__item-label--time">
                  {moment(milestoneDetails[key].updatedAt).format('YYYY-MM-DD HH:mm:ss')}
                </p>
              </If>

              <If
                condition={
                  milestoneDetails[key]?.countdown &&
                  [
                    instantBookOrderStage.SIGNING_AGREEMENT,
                    instantBookOrderStage.MAKING_PAYMENT,
                  ].includes(key)
                }
              >
                <Countdown
                  className="instant-process-bar__countdown"
                  initTime={milestoneDetails[key].countdown.initTime}
                  t={t}
                  tooltip={t(`landlord.longtail_booking.process.tooltip.${key}`, {
                    dueHours,
                  })}
                  dueHours={dueHours}
                />
              </If>
            </div>
          </li>
        </For>
      </ul>
    </div>
  );
};

export default InstantBookProcessBar;
